import { useDataManagementFileHandler } from "@/hooks/use-data-management-file-handler";
import { ImportDroneDataCard } from "@/modes/create-area-mode/import-drone-data-card";
import { ImportScansCard } from "@/modes/create-area-mode/import-scans-card";
import { useAppSelector } from "@/store/store-hooks";
import { selectCanUsePhotogrammetry } from "@/store/subscription-features/subscription-features-selectors";
import { selectCurrentUser } from "@/store/user-selectors";
import { DataManagementDropzone } from "@faro-lotv/data-management";
import { Dialog, FaroText, grey, useBreakpointMdUp } from "@faro-lotv/flat-ui";
import { selectCompanyId, selectProjectId } from "@faro-lotv/project-source";
import { Grid } from "@mui/material";
import { useCallback, type JSX } from "react";
import { create } from "zustand";

type DraftImportDataDialogStore = {
  /** Flag whether the Draft View's import data dialog is open. */
  isOpen: boolean;
  /** Open or close the Draft View's import data dialog. */
  setIsOpen(isOpen: boolean): void;
};

/** A small zustand store holding the open state of the Draft View's import data dialog. */
export const useDraftImportDataDialogStore =
  create<DraftImportDataDialogStore>()((set) => ({
    isOpen: false,
    setIsOpen: (isOpen) => set({ isOpen }),
  }));

/**
 * @returns Simplified variant of the Live View's ImportDataDialog for the Draft View, which only
 * offers the data types that can be imported by the Data Management editor.
 *
 * Rendered outside of the sidebar that contains the button opening it, so that it stays open when
 * the sidebar gets unmounted, e.g. when the browser window is resized to a small width.
 *
 * !!! When adjusting this component, check if the Live View's ImportDataDialog needs to be
 *     adjusted as well. !!!
 */
export function DraftImportDataDialog(): JSX.Element {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = useAppSelector(selectCompanyId);
  const projectId = useAppSelector(selectProjectId);
  const canUsePhotogrammetry = useAppSelector(selectCanUsePhotogrammetry);
  const isMdUp = useBreakpointMdUp();

  const isOpen = useDraftImportDataDialogStore((state) => state.isOpen);
  const setIsOpen = useDraftImportDataDialogStore((state) => state.setIsOpen);
  const onClose = useCallback(() => setIsOpen(false), [setIsOpen]);

  // File checking for DataManagementDropzone. The import cards have their own calls.
  const {
    isCheckingFiles,
    setIsCheckingFiles,
    onFinishedCheckingFiles,
    fileTypesByCategory,
  } = useDataManagementFileHandler({
    projectId,
    from: "draftImportScansDialog",
    onClose,
  });

  return (
    <Dialog
      title="Bring your data into the cloud"
      open={isOpen}
      onClose={onClose}
      size="l"
      sx={{
        // Set the z-index to achieve the following stacking of UI elements:
        //  1) Toasts: snackbar = 1400
        //  2) MainUi > ModalSpinner: snackbar - 1 = 1399
        //  3) Data Management > DataCategoryDialog: snackbar - 2 = 1398
        //  4) ImportDataDialog > Dialog: snackbar - 3 = 1397
        //  5) NewFeaturePopup > FaroPopover: modal + 1 = 1301
        zIndex: (theme) => theme.zIndex.snackbar - 3,
        "& .MuiDialog-container > .MuiPaper-root": {
          backgroundColor: grey[50],
          borderRadius: 4,
        },
      }}
    >
      {currentUser && companyId && projectId ? (
        <DataManagementDropzone
          currentUser={currentUser}
          companyId={companyId}
          projectId={projectId}
          fileTypesByCategory={fileTypesByCategory}
          isChecking={isCheckingFiles}
          setIsChecking={setIsCheckingFiles}
          onFinished={onFinishedCheckingFiles}
        />
      ) : null}

      <FaroText variant="bodyL" color={grey[600]} sx={{ mb: 3 }}>
        Drag & drop your files or choose the type of data you want to import
      </FaroText>

      <Grid
        container
        spacing={2}
        rowSpacing={2}
        sx={{
          width: !canUsePhotogrammetry && isMdUp ? "33%" : "67%",
          mx: "auto",
          mb: 1,
          px: 1,
        }}
      >
        <Grid key={0} size={canUsePhotogrammetry && isMdUp ? 6 : 12}>
          <ImportScansCard
            from="draftImportScansDialog"
            // It's not necessary to pass onClose to the import cards in the Live View's ImportDataDialog
            // because they navigate away from the page with navigateToDraftView in onFinishedCheckingFiles.
            // In the Draft View, the target route is identical, so an explicit onClose call is needed.
            onClose={onClose}
          />
        </Grid>
        {canUsePhotogrammetry && (
          <Grid key={1} size={isMdUp ? 6 : 12}>
            <ImportDroneDataCard
              from="draftImportScansDialog"
              onClose={onClose}
            />
          </Grid>
        )}
      </Grid>
    </Dialog>
  );
}
